<script>
import * as m from '@app/paraglide/messages.js';
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { audioScrub } from "@app/store/audioScrubStore.svelte.js";
import { onMount } from 'svelte';
import tcLib from "@cclib/lib/timecode.js";

/**
 * @typedef {Object} Props
 * @property {any} event
 * @property {string} [tcType]
 * @property {boolean} [enabled]
 */

/** @type {Props} */
let { event, tcType = "start", enabled = true } = $props();
let tc = $state("--:--:--:--");

onMount(() =>{
    try {
        if (
            !event[tcType] &&
            event[tcType] !== 0
        ) {
            tc = "--:--:--:--";
        } else {
            tc = tcLib.secToTc(
                event[tcType] + projectState.incode,
                projectState.frameRate,
                projectState.dropFrame,
            );
        }
    } catch (err) {
        tc = "--:--:--:--";
    }
});


function setTc(e) {
    e.preventDefault();
    e.stopPropagation();

    let originalDuration =
        event.end ===
            false ||
        event.start ===
            false
            ? 2
            : event.end -
              event.start;

    let originalId =
        event.id;

    event[tcType] =
        player.currentTime;
    tc = tcLib.secToTc(
        player.currentTime + projectState.incode,
        projectState.frameRate,
        projectState.dropFrame,
    );

    let eventIndex = eventGroupState[projectState.selected].events.findIndex((e) => e.id === originalId);
    if (tcType === "start") {
        if (
            isNaN(event.end)
        ) {
            event.end =
                player.currentTime + 2;
        } else if (
            event.end <
            event.start
        ) {
            event.end =
                event
                    .start + originalDuration;
        }

        //If the minimum frame gap is set and there is an overlap with the previoud event in the eventgroup then adjust the end time of the previous event
        if (
            editorState.minFrameGap > 0 &&
            eventIndex > 0 &&
            event.start <=
                eventGroupState[projectState.selected].events[eventIndex - 1]
                    .end
        ) {
            eventGroupState[projectState.selected].events[eventIndex - 1].end =
                event
                    .start -
                editorState.minFrameGap / projectState.frameRate;
        }
    } else if (tcType === "end") {
        if (
            isNaN(
                event.start,
            )
        ) {
            event.start =
                player.currentTime - 2;
        } else if (
            event.start >
            event.end
        ) {
            event.start =
                event.end -
                originalDuration;
        }

        //If the minimum frame gap is set and there is an overlap with the next event in the eventgroup then adjust the start time of the next event
        if (
            editorState.minFrameGap > 0 &&
            eventIndex <
                eventGroupState[projectState.selected].events.length - 1 &&
            event.end >=
                eventGroupState[projectState.selected].events[eventIndex + 1]
                    .start
        ) {
            eventGroupState[projectState.selected].events[
                eventIndex + 1
            ].start =
                event.end +
                editorState.minFrameGap / projectState.frameRate;
        }
    }

    e.target.blur();
    //Select the event that was just updated
    let newIndex = eventGroupState[projectState.selected].events.findIndex(
        (event) => event.id === originalId,
    );
    eventGroupState[projectState.selected].selected = [newIndex];

    insertHistoryAction({
        name: "update timecode", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });
}

function validateTc(e) {
    if (e.target.value === "--:--:--:--") {
        return;
    }

    tc = tcLib.tcValidate(
        e.target.value,
        projectState.frameRate,
        projectState.dropFrame,
    );

    event[tcType] =
        tcLib.tcToSec(tc, projectState.frameRate, projectState.dropFrame) -
        projectState.incode;

    e.target.blur();

    /* Save to undo/redo history */
    insertHistoryAction({
        name: "update timecode", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });
}

function updateCurrentTime(e) {
    e.preventDefault();
    e.stopPropagation();
    if (lockState.video && player) {
        player.currentTime =
            event[tcType];
    }
}

function frameByFrame(e) {
    if (e.key === "ArrowUp") {
        event[tcType] +=
            tcLib.framesToSec(1, projectState.frameRate);
        tc = tcLib.secToTc(
            event[tcType] + projectState.incode,
            projectState.frameRate,
            projectState.dropFrame,
        );

        if (lockState.video && player) {
            player.currentTime =
                event[
                    tcType
                ];
            audioScrub();
        }
    } else if (e.key === "ArrowDown") {
        event[tcType] -=
            tcLib.framesToSec(1, projectState.frameRate);
        tc = tcLib.secToTc(
            event[tcType] + projectState.incode,
            projectState.frameRate,
            projectState.dropFrame,
        );

        if (lockState.video && player) {
            player.currentTime =
                event[
                    tcType
                ];
            audioScrub();
        }
    }
}

function updateTc(sec) {
    if (!sec && sec !== 0) {
        tc = "--:--:--:--";
        return;
    }

    tc = tcLib.secToTc(
        (parseFloat(sec) + parseFloat(projectState.incode)).toFixed(3),
        projectState.frameRate,
        projectState.dropFrame,
    );
}

function triggerTcUpdate(frameRate, incode) {
    if (event[tcType] === undefined || event[tcType] === null) {
        return;
    }

    updateTc(event[tcType]);
}

$effect(() => {
    // console.log("EFFECT Syncing TC Input with Event Group State...");
    triggerTcUpdate(projectState.frameRate, projectState.incode);
});
</script>

<button
    id="tc-set-button-{tcType}"
    aria-label={m.input_tc_set_button_aria_label({tcType})}
    disabled={!enabled}
    class="btn btn-secondary"
    type="button"
    title={m.input_tc_set_button_title({tcType})}
    onclick={setTc}>
    <i class="bi bi-clock"></i></button>
<input
    id="tc-input-{tcType}"
    aria-label={m.input_tc_input_aria_label({tcType})}
    disabled={!enabled}
    class="form-control border"
    type="text"
    onclick={(e) => updateCurrentTime(e)}
    onkeydown={frameByFrame}
    placeholder={tcType}
    bind:value={tc}
    onblur={validateTc}
    onkeyup={(e) => e.key === "Enter" && validateTc(e)}
    onfocus={(e) => {
        e.target.select();
    }}
    title={tc} />

<style>
button,
input {
    font-size: 0.9vw !important;
    padding: 3px 6px !important;
}
</style>
